import { Box, Skeleton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getFirstDayOfMonth, getUnixTimestamp } from "../../utils";

import { IOptionGeneric } from "../../shared/interface";
import Loading from "../Loading/Loading";
import SelectableMonth from "../SelectableMonthCalendar/SelectableMonth";
import SelectableTimes from "../SelectableTimes/SelectableTimes";
import { fromUnixToDateString } from "../../utils/unix-timestamp";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useAvailableTimes } from "../../services/available-times/available-times.service";
import { useModal } from "../../context/ModalContext";
import { useUserDetail } from "../../services/user-detail/user-detail.service";
import useWebsiteUserAppointment from "../../services/website-user-appointment/website-user-appointment.service";
import { useAvailableDropoffTimes } from "../../services/drop-off-appointment/drop-off-appointment.service";
import { useAllServiceStatusStore, useBookingAppointmentTypeStore } from "../../hooks";
import { ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, MONTHS_NAME } from "../../config/params";
import CheveronDownSmallIcon from "../Icons/CheveronDownSmallIcon";
import PetAvatars from "../PetAvatars/PetAvatars";
import { formatPetNames } from "./ExpressAvailableTimeSlots";
import { CalendarIcon } from "../Icons";

interface IAvailableTimeSlots {
  title?: string;
  date?: number;
  isSelectable?: boolean;
  icon?: React.ElementType<any>;
  isCarousal?: boolean;
  isPreviewModal?: boolean;
  isDropoff?: boolean;
  isRescheduleModal?: boolean;
}

const AvailableTimeSlots: React.FC<IAvailableTimeSlots> = ({
  title,
  icon,
  date,
  isSelectable,
  isCarousal = true,
  isPreviewModal,
  isDropoff=false,
  isRescheduleModal=false,
}) => {
  const { user } = useUserDetail();
  const todayEpoch = getUnixTimestamp(new Date());
  const [selectedDateEpochFormat, setSelectedDateEpochFormat] =
    useState<number>();
  const { modalView } = useModal();
  const { reasonForVisit, setAppointmentsForm, selectedPets, appointmentForm } =
    useAppointmentStore();
  const [autoMonthChangeAttempts, setAutoMonthChangeAttempts] = useState(0);
  const { getUserAppointment, webUserAppointment } =
    useWebsiteUserAppointment();
  const {
    getAvailableTimes,
    timesList: availableTimesSlots,
    loading,
    daysOfMonthList: fullMonthDaysList,
    appointmentType,
  } = useAvailableTimes();
  const {
    getAvailableDropoffTimes,
    dropoffTimesList,
    loading: dropOffLoading,
    daysOfMonthDropoffList,
    dropoffAppointmentType,
  }=useAvailableDropoffTimes();
  const unixDateRef = React.useRef<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [disableSkip, setDisableSkip] = useState<boolean>(false);
  const { setAppointmentTypeIndicator, bookingAppointmentType,
    setBookingAppointmentType } = useBookingAppointmentTypeStore();
  const { isDropOffServiceAvailable }=useAllServiceStatusStore();


  function getFormattedDateFromEpoch(epoch: number): string {
    const date = new Date(fromUnixToDateString(epoch, "GMT"));
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString("en-US", options);
  }

  function getFormattedDayFromEpoch(epoch: number): string {
    const date = new Date(fromUnixToDateString(epoch, "GMT"));
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
    };

    return date.toLocaleDateString("en-US", options);
  };

  const handleDateSelected = (date: Date) => {
    handleDateSelection(date);
    setSelectedDateEpochFormat(getUnixTimestamp(date));
    // setIsMonthChange(false);
    unixDateRef.current = getUnixTimestamp(date);
  };

  const handleDateSelection = (date: Date) => {
    setAppointmentsForm(null);
    setSelectedDate(MONTHS_NAME[date.getMonth()] + " " + date.getDate());
  };

  // when user land on this page finding if user coming from
  // website and selected slot on website or not
  useEffect(() => {
    if (localStorage.getItem("isWebUser") !== "undefined") {
      const isWebUser = JSON.parse(localStorage.getItem("isWebUser") || "{}");

      modalView && localStorage.setItem("isWebUser", "{}");
      if (isWebUser?.nextScreen === "bookAVisit") {
        getUserAppointment();
      }
    }
  }, []);

  // when user change location then we need to show updated location slots
  // and when user change date then automatically call api for slots
  useEffect(() => {
    if (user?.location?.idLocation) {
      if (isDropoff) {
        getAvailableDropoffTimes({
          reasons: { ...reasonForVisit },
          date: selectedDateEpochFormat?.toString()!,
          isPreviewModal,
        });
      } else {
        getAvailableTimes({
          reasons: { ...reasonForVisit },
          date: selectedDateEpochFormat?.toString()!,
          isPreviewModal,
        });
      }
    }
  }, [selectedDateEpochFormat, user?.location, selectedPets.length]);


  // When number of pets change then call available time api
  useEffect(() => {
    if (user?.location?.idLocation && selectedDateEpochFormat) {
      if (isDropoff) {
        getAvailableDropoffTimes({
          reasons: { ...reasonForVisit },
          date: selectedDateEpochFormat?.toString()!,
          isPreviewModal,
        });
      } else {
        getAvailableTimes({
          reasons: { ...reasonForVisit },
          date: selectedDateEpochFormat?.toString()!,
          isPreviewModal,
        });
      }
    }
  }, [selectedPets.length, isDropoff]);

  const handleSlotSelection = (option: IOptionGeneric) => {
    if (user?.location?.idLocation && option) {
      setAppointmentsForm({
        time: option.value,
        location: user?.location?.idLocation,
        resourcesId: option.resourcesId,
        selectedSlotLabel: option.label,
        day: selectedDate,
      });
    }
  };

  const handleMonthChange = (newMonth: Date) => {
    const dateUnix = getUnixTimestamp(getFirstDayOfMonth(newMonth));

    setSelectedDateEpochFormat(dateUnix);
    // setIsMonthChange(true);
  };

  // this useEffect responsible for fetchng slot if not found on current
  // date or current month
  useEffect(() => {
    const appointmentDaysList = isDropoff ? daysOfMonthDropoffList : fullMonthDaysList;
    const appointmentTimesList = isDropoff ? dropoffTimesList : availableTimesSlots;

    if (dropoffAppointmentType && isDropoff) {
      setAppointmentTypeIndicator(dropoffAppointmentType);
    }

    if (appointmentType && !isDropoff) {
      setAppointmentTypeIndicator(appointmentType);
    }


    if (!disableSkip && appointmentDaysList.length && !appointmentTimesList.length) {
      const nearestAvailableDate = appointmentDaysList.filter((day: any) => {
        return !day.disabled;
      });

      if (nearestAvailableDate.length) {
        setSelectedDateEpochFormat(+nearestAvailableDate[0].value);
        setDisableSkip(true);
      }
      if (autoMonthChangeAttempts < 5 && selectedDateEpochFormat && !nearestAvailableDate.length) {
        /* instead of using getMonth/ getFullYear,
         we can use getUTCMonth/ getUTCFullYear to avoid timeZone issue
         which is causing our month to not move forward and getting stuck
          after 1-2 iteration */

        const currentDate = new Date(selectedDateEpochFormat * 1000);
        const currentMonth = currentDate.getUTCMonth();
        const nextMonth = currentMonth + 1;

        const nextYear = nextMonth > 11 ? currentDate.getUTCFullYear() + 1 : currentDate.getUTCFullYear();

        const nextMonthFirstDate = new Date(nextYear, nextMonth % 12, 1);

        setAutoMonthChangeAttempts(autoMonthChangeAttempts + 1);
        handleMonthChange(nextMonthFirstDate);
      }
    }
  }, [fullMonthDaysList, availableTimesSlots, daysOfMonthDropoffList, dropoffTimesList]);

  // this useEffect set initial date to fetch slots if user coming back step
  // from reason for visit or from pet selection
  useEffect(() => {
    if (appointmentForm?.time) {
      setSelectedDateEpochFormat(+appointmentForm?.time);
    } else {
      setSelectedDateEpochFormat(date ? date : todayEpoch);
    }
  }, []);

  useEffect(() => {
    if (isDropoff) {
      setAutoMonthChangeAttempts(0);
      setDisableSkip(false);
    }
  }, [isDropoff]);

  // this useEffect responsible for fetching data of that date that
  // user already selected on website
  useEffect(() => {
    if (webUserAppointment) {
      if (isDropOffServiceAvailable &&
        (webUserAppointment.appointmentCreateType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING)) {
        setAppointmentTypeIndicator(ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING);
        setBookingAppointmentType(CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
      }
      setSelectedDateEpochFormat(webUserAppointment.start_at);
    }
  }, [webUserAppointment, availableTimesSlots, dropoffTimesList]);

  // setting selected date to redux appointment form
  // to show on panel in booking flow
  useEffect(() => {
    if (selectedDateEpochFormat) {
      setSelectedDate(getFormattedDateFromEpoch(+selectedDateEpochFormat));
    } else if (selectedDateEpochFormat) {
      setSelectedDate(getFormattedDateFromEpoch(+selectedDateEpochFormat));
    }
  }, [selectedDateEpochFormat]);

  // useEffect handling webuser slot selection
  // need to handle dropoff web user slot selection
  useEffect(() => {
    if (webUserAppointment) {
      let slotSelectedOnWebsite: IOptionGeneric | undefined;

      if (isDropoff) {
        slotSelectedOnWebsite = dropoffTimesList.find(
          (data: IOptionGeneric) => +data.value == webUserAppointment.start_at,
        );
      } else {
        slotSelectedOnWebsite = availableTimesSlots.find(
          (data: IOptionGeneric) => +data.value == webUserAppointment.start_at,
        );
      }

      if (slotSelectedOnWebsite) {
        if (isDropOffServiceAvailable &&
          (webUserAppointment.appointmentCreateType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING)) {
          setAppointmentTypeIndicator(ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING);
          setBookingAppointmentType(CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
        }
        handleSlotSelection(slotSelectedOnWebsite);
      }
    }
  }, [webUserAppointment, availableTimesSlots, dropoffTimesList]);

  const dateElement = <div className="flex gap-2 items-center">
    <CalendarIcon color="#262626" />
    {selectedDateEpochFormat?.toString() === todayEpoch.toString() ? (
      <>
        <span className="font-semibold">Today, </span>
        <span>{getFormattedDateFromEpoch(+selectedDateEpochFormat!)}</span>
      </>
    ) : selectedDateEpochFormat?.toString() !== "" && !loading && !dropOffLoading ? (
      <>
        <span className="font-semibold">{getFormattedDayFromEpoch(+selectedDateEpochFormat!)}, </span>
        <span>{getFormattedDateFromEpoch(+selectedDateEpochFormat!)}</span>
      </>
    ) : (
      <Box sx={{ width: 80, height: 20, ml: "0.2rem" }}>
        <Skeleton animation="wave" />
      </Box>
    )}
  </div>;

  return (
    <div className={`h-full grow flex flex-col p-4 gap-4
    ${!isRescheduleModal ? " border border-taupe-50 rounded-xl" : ""}`}>
      {!isRescheduleModal &&
            <div className="flex justify-between items-center gap-2">
              <p className="text-lg ">
                Select a time for&nbsp;
                <span className="font-semibold">{formatPetNames(selectedPets.map((pet, index)=>pet.name ))}</span>
                &nbsp;<span>{bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ?
                  "Drop-Off Visit." : "Doctor Visit."}</span>
              </p>
              <PetAvatars
                height={50}
                width={50}
                petDetails={selectedPets} />
            </div>}
      <SelectableMonth
        onSelected={handleDateSelected}
        icon={icon ? icon : CheveronDownSmallIcon}
        fullMonthDaysList={isDropoff ? daysOfMonthDropoffList : fullMonthDaysList}
        currentDay={selectedDateEpochFormat}
        handleMonthChange={handleMonthChange}
        loading={loading || dropOffLoading}
        dateElement={dateElement}
      />
      <Box className="border-b-2 border-gray-3 w-full"></Box>
      <div className="h-full">
        {!loading && !dropOffLoading &&
        ((!isDropoff && availableTimesSlots?.length) || (isDropoff && dropoffTimesList?.length)) ? (
            <SelectableTimes
              value={appointmentForm?.time}
              options={isDropoff ? dropoffTimesList : availableTimesSlots}
              onSelected={(option: IOptionGeneric) => {
                handleSlotSelection(option);
              }}
              isSelectable={isSelectable}
              carousel={isCarousal}
            />
          ) : (
            <div className="flex h-full w-full justify-center items-center">
              {!loading && !dropOffLoading ? (
                <div className="text-sm text-gray-1 py-4">
                We’re fully booked for this day.
                </div>
              ) : (
                <Loading />
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default AvailableTimeSlots;
